import { forwardRef, useEffect, useRef } from 'react';
import { ArrowUp } from 'lucide-react';
import { PRIVACY_URL } from './constants';

/**
 * The question box, its send button, and the standing disclosure line. The disclosure is a
 * consent requirement (docs/15 C4: always visible, acknowledged or not), so it lives here
 * rather than in each surface — that way no surface can forget it.
 */

const cls = {
  inputRow: 'flex items-end gap-2',
  textarea: 'min-w-0 flex-1 resize-none rounded-token border border-line-strong bg-transparent px-3 py-2 text-[13px] text-navy outline-none focus:border-bronze-strong focus:ring-1 focus:ring-bronze-strong disabled:opacity-50',
  sendBtn:  'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-none cursor-pointer bg-navy text-cream disabled:opacity-40 disabled:cursor-default self-center',
  stopBtn:  'h-8 shrink-0 self-center rounded-full border border-line-strong bg-transparent px-3 text-[12px] font-medium text-navy cursor-pointer hover:bg-black/5',
  disclosure: 'mt-2 text-[11px] leading-relaxed text-slate',
  disclosureLink: 'text-bronze-text underline underline-offset-2',
};

interface ComposerProps {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  /** Any reason the composer is inert: sending, at the monthly limit, or not yet consented. */
  disabled: boolean;
  /** Distinguishes "can't type right now" from "can't type this month" in the placeholder. */
  limitReached: boolean;
  /** While a question is pending, the send button gives way to Stop. Omit to never show it. */
  onStop?: () => void;
  /** True while a question is pending. */
  sending?: boolean;
  rows?: number;
}

export const Composer = forwardRef<HTMLTextAreaElement, ComposerProps>(function Composer(
  { value, onChange, onSend, disabled, limitReached, onStop, sending = false, rows = 2 }, ref,
) {
  // Stop re-enables the textarea; focus goes back to it once it is enabled again.
  const innerRef = useRef<HTMLTextAreaElement | null>(null);
  const focusAfterStop = useRef(false);
  useEffect(() => {
    if (!sending && focusAfterStop.current) {
      focusAfterStop.current = false;
      innerRef.current?.focus();
    }
  }, [sending]);
  const setRefs = (el: HTMLTextAreaElement | null) => {
    innerRef.current = el;
    if (typeof ref === 'function') ref(el);
    else if (ref) ref.current = el;
  };

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  }

  return (
    <>
      <div className={cls.inputRow}>
        <textarea
          ref={setRefs}
          className={cls.textarea}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={limitReached ? 'Monthly limit reached' : 'Ask a question…'}
          disabled={disabled}
          aria-label="Ask the assistant a question"
        />
        {sending && onStop ? (
          <button type="button" onClick={() => { focusAfterStop.current = true; onStop(); }} className={cls.stopBtn}>
            Stop
          </button>
        ) : (
        <button
          onClick={onSend}
          disabled={disabled || !value.trim()}
          aria-label="Send"
          className={cls.sendBtn}
        >
          <ArrowUp size={15} strokeWidth={2} aria-hidden />
        </button>
        )}
      </div>
      <p className={cls.disclosure}>
        Answers are AI-generated from your workspace data and may contain errors. Verify
        important figures.{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={cls.disclosureLink}>
          How we handle AI data
        </a>
      </p>
    </>
  );
});
